import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';

// Landing page components
import Header from './components/Header';
import Hero from './components/Hero';
import Services from './components/Services';
import Works from './components/Works';
import Process from './components/Process';
import Portal from './components/Portal';
import WhyUs from './components/WhyUs';
import About from './components/About';
import Contact from './components/Contact';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';

// Portal pages
import LoginPage from './pages/LoginPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PortalLayout from './pages/PortalLayout';
import ForbiddenPage from './pages/ForbiddenPage';

// Client pages
import ClientDashboard from './pages/client/Dashboard';
import ProjectsList from './pages/client/ProjectsList';
import JobsList from './pages/client/JobsList';
import JobDetail from './pages/client/JobDetail';
import DocumentsList from './pages/client/DocumentsList';
import MessagesList from './pages/client/MessagesList';
import ContactPage from './pages/client/ContactPage';

// Shared pages
import ProjectDetail from './pages/shared/ProjectDetail';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import ClientsManagement from './pages/admin/ClientsManagement';
import ProjectsManagement from './pages/admin/ProjectsManagement';
import AdminJobsManagement from './pages/admin/JobsManagement';
import AdminVisitors from './pages/admin/VisitorsAnalytics';
import TestimonialsManagement from './pages/admin/TestimonialsManagement';
import EmployeesManagement from './pages/admin/EmployeesManagement';
import EmployeeTasksManagement from './pages/admin/EmployeeTasksManagement';
import WorkTypesManagement from './pages/admin/WorkTypesManagement';
import FinanceManagement from './pages/admin/FinanceManagement';
import AuditLogs from './pages/admin/AuditLogs';
import WorkEntriesManagement from './pages/admin/WorkEntriesManagement';
import MileageManagement from './pages/admin/MileageManagement';
import SalaryCalculation from './pages/admin/SalaryCalculation';
import ProjectProfit from './pages/admin/ProjectProfit';
import TaxSettings from './pages/admin/TaxSettings';
import ProjectsMap from './pages/admin/ProjectsMap';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TaskLinkPage from './pages/TaskLinkPage';

// Employee pages
import EmployeeHome from './pages/employee/EmployeeHome';
import EmployeeTasks from './pages/employee/EmployeeTasks';
import EmployeeTaskDetail from './pages/employee/EmployeeTaskDetail';
import EmployeeEntries from './pages/employee/EmployeeEntries';

// Landing page
const LandingPage = () => {
  return (
    <>
      <Header />
      <CookieConsent />
      <main className="w-full overflow-x-hidden">
        <Hero />
        <Services />
        <Works />
        <Process />
        <Portal />
        <WhyUs />
        <About />
        <Contact />
      </main>
      <Footer />
    </>
  );
};

// Protected route wrapper
const ProtectedRoute = ({ children, requireAdmin = false, requireEmployee = false }) => {
  const { isAuthenticated, isAdmin, isEmployee, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/portal/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <ForbiddenPage />;
  }

  if (requireEmployee && !isEmployee) {
    return <ForbiddenPage />;
  }

  return children;
};



function AppRoutes() {
  return (
    <Routes>
      {/* Landing page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Portal login */}
      <Route path="/portal/login" element={<LoginPage />} />
      <Route path="/portal/forgot-password" element={<ForgotPassword />} />
      <Route path="/portal/reset-password" element={<ResetPassword />} />

      {/* Privacy policy */}
      <Route path="/privatuma-politika" element={<PrivacyPolicy />} />

      {/* Direct task link */}
      <Route path="/task/:code" element={<TaskLinkPage />} />
      
      {/* 403 page */}
      <Route path="/portal/forbidden" element={<ForbiddenPage />} />

      {/* Client portal routes */}
      <Route path="/portal" element={
        <ProtectedRoute>
          <PortalLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/portal/dashboard" replace />} />
        <Route path="dashboard" element={<ClientDashboard />} />
        <Route path="projects" element={<ProjectsList />} />
        <Route path="projects/:projectId" element={<ProjectDetail />} />
        <Route path="jobs" element={<JobsList />} />
        <Route path="jobs/:jobId" element={<JobDetail />} />
        <Route path="documents" element={<DocumentsList />} />
        <Route path="messages" element={<MessagesList />} />
        <Route path="contact" element={<ContactPage />} />
      </Route>

      {/* Employee portal routes */}
      <Route path="/portal/employee" element={
        <ProtectedRoute requireEmployee>
          <PortalLayout />
        </ProtectedRoute>
      }>
        <Route index element={<EmployeeHome />} />
        <Route path="tasks" element={<EmployeeTasks />} />
        <Route path="tasks/:taskId" element={<EmployeeTaskDetail />} />
        <Route path="entries" element={<EmployeeEntries />} />
      </Route>

      {/* Admin portal routes */}
      <Route path="/portal/admin" element={
        <ProtectedRoute requireAdmin>
          <PortalLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="clients" element={<ClientsManagement />} />
        <Route path="employees" element={<EmployeesManagement />} />
        <Route path="employee-tasks" element={<EmployeeTasksManagement />} />
        <Route path="projects" element={<ProjectsManagement />} />
        <Route path="projects/:projectId" element={<ProjectDetail />} />
        <Route path="map" element={<ProjectsMap />} />
        <Route path="jobs" element={<AdminJobsManagement />} />
        <Route path="documents" element={<DocumentsList />} />
        <Route path="messages" element={<MessagesList />} />
        <Route path="visitors" element={<AdminVisitors />} />
        <Route path="testimonials" element={<TestimonialsManagement />} />
        <Route path="audit" element={<AuditLogs />} />
        <Route path="work-types" element={<WorkTypesManagement />} />
        <Route path="work-entries" element={<WorkEntriesManagement />} />
        <Route path="mileage" element={<MileageManagement />} />
        <Route path="salary" element={<SalaryCalculation />} />
        <Route path="profit" element={<ProjectProfit />} />
        <Route path="tax-settings" element={<TaxSettings />} />
        <Route path="finance" element={<FinanceManagement />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <Toaster position="top-center" />
            <AppRoutes />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
